import React, { useState, useEffect, useCallback } from 'react';
import { useBrand } from '../../context/BrandContext';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  Paper
} from '@mui/material';
import {
  Link as LinkIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import {
  getReconciliationUnmappedSkus,
  getReconciliationUnmappedSummary,
  mapReconciliationSku,
  getInventorySkus,
  runReconciliation,
  formatNumber
} from '../../services/inventoryService';

/**
 * ReconciliationUnmappedSkus Component
 * Shows SKUs from shipments that failed batch reconciliation due to missing inventory mapping.
 * Allows mapping to existing batch inventory SKUs.
 */
function ReconciliationUnmappedSkus() {
  const { selectedBrand } = useBrand();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [unmappedSkus, setUnmappedSkus] = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [total, setTotal] = useState(0);
  // Which bucket the table shows: 'all', 'unknown' (SKU absent from product_master /
  // product_identifiers - the only bucket "Map" can fix), or 'known' (a known SKU that
  // shipped with no received batch at ship time - a receiving/stock-timing gap).
  const [bucket, setBucket] = useState('all');

  // Mapping dialog state
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [selectedUnmappedSku, setSelectedUnmappedSku] = useState(null);
  const [inventorySkuOptions, setInventorySkuOptions] = useState([]);
  const [selectedInventorySku, setSelectedInventorySku] = useState(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [inventorySkuLoading, setInventorySkuLoading] = useState(false);

  // Reconciliation run state
  const [reconciliationRunning, setReconciliationRunning] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [skusRes, summaryRes] = await Promise.all([
        getReconciliationUnmappedSkus({ limit: 200, bucket }),
        getReconciliationUnmappedSummary()
      ]);

      setUnmappedSkus(skusRes?.data?.unmapped_skus || []);
      // The endpoint returns `count`, not `total` (batch-controller.js getReconciliationUnmappedSkus).
      // Reading `total` always yielded undefined -> 0, so the header rendered
      // "0 Unmapped SKUs from Shipments" / "Showing 200 of 0" directly above 200 populated rows.
      setTotal(skusRes?.data?.count ?? skusRes?.data?.total ?? 0);
      setSummary(summaryRes?.data || null);
    } catch (err) {
      console.error('Failed to load reconciliation unmapped SKUs:', err);
      setError('Failed to load unmapped SKUs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [bucket]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Open mapping dialog for an unmapped SKU
  const openMappingDialog = async (unmappedSku) => {
    setSelectedUnmappedSku(unmappedSku);
    setSelectedInventorySku(null);
    setInventorySkuOptions([]);
    setError(null); setMappingDialogOpen(true);

    // Load inventory SKUs for dropdown
    setInventorySkuLoading(true);
    try {
      const params = selectedBrand?.id ? { brand_id: selectedBrand.id } : {};
      const res = await getInventorySkus(params);
      setInventorySkuOptions(res?.data || []);
    } catch (err) {
      console.error('Failed to load inventory SKUs:', err);
      setError('Failed to load inventory SKUs for mapping');
    } finally {
      setInventorySkuLoading(false);
    }
  };

  // Submit SKU mapping
  const handleSubmitMapping = async () => {
    if (!selectedUnmappedSku || !selectedInventorySku) {
      setError('Please select an inventory SKU to map to');
      return;
    }

    setMappingLoading(true);
    try {
      await mapReconciliationSku({
        unmapped_sku_id: selectedUnmappedSku.id,
        mapped_to_sku: selectedInventorySku.primary_sku
      });

      setSuccess(`SKU "${selectedUnmappedSku.sku}" has been mapped to "${selectedInventorySku.primary_sku}"`);
      setMappingDialogOpen(false);

      // Reload data to refresh the list
      loadData();
    } catch (err) {
      console.error('Failed to map SKU:', err);
      setError(err.response?.data?.error || 'Failed to create SKU mapping');
    } finally {
      setMappingLoading(false);
    }
  };

  // Run reconciliation again after mappings
  const handleRunReconciliation = async () => {
    setReconciliationRunning(true);
    try {
      const res = await runReconciliation({ limit: 5000 });
      setSuccess(`Reconciliation complete. Processed ${res?.data?.processed || 0} shipments.`);
      loadData();
    } catch (err) {
      console.error('Failed to run reconciliation:', err);
      setError(err.response?.data?.error || 'Failed to run reconciliation');
    } finally {
      setReconciliationRunning(false);
    }
  };

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Filter unmapped SKUs by search term
  const filteredSkus = unmappedSkus.filter(sku =>
    !searchTerm ||
    sku.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sku.warehouse_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Two-bucket summary: what actually needs mapping vs a receiving/stock gap.
          Most "unmapped" rows have reason=no_batches_received_before_ship_date on a KNOWN
          SKU, which "SKU Mappings" cannot fix - so we surface the two problems separately. */}
      {summary && (() => {
        const unknownSkus = Number(summary.unknown_sku_skus) || 0;
        const unknownUnits = Number(summary.unknown_sku_units) || 0;
        const knownSkus = Number(summary.known_no_batch_skus) || 0;
        const knownUnits = Number(summary.known_no_batch_units) || 0;
        return (
          <>
            {unknownSkus > 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon />
                    {formatNumber(unknownSkus)} unknown SKU{unknownSkus === 1 ? '' : 's'} to map ({formatNumber(unknownUnits)} units)
                  </Box>
                </AlertTitle>
                <Typography variant="body2">
                  These SKUs are absent from product_master AND product_identifiers. Map each to an
                  existing inventory SKU (or create it) so reconciliation can deduct its stock.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                <AlertTitle>No unknown SKUs to map</AlertTitle>
                <Typography variant="body2">
                  Every shipped SKU below is a known product - nothing here needs a SKU mapping.
                </Typography>
              </Alert>
            )}
            {knownSkus > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>
                  {formatNumber(knownSkus)} known SKU{knownSkus === 1 ? '' : 's'} shipped with no received batch at ship time ({formatNumber(knownUnits)} units)
                </AlertTitle>
                <Typography variant="body2">
                  These are known products, so this is a receiving/stock-timing gap - a shipment went
                  out before any batch was received for it - NOT a SKU-mapping problem. Fix it by
                  recording the missing receipts (or correcting receipt dates), not via SKU Mappings.
                </Typography>
              </Alert>
            )}
            {/* Filter the table by bucket */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {[
                { key: 'all', label: 'All' },
                { key: 'unknown', label: `Needs mapping (${formatNumber(unknownSkus)})` },
                { key: 'known', label: `Known - stock gap (${formatNumber(knownSkus)})` }
              ].map(opt => (
                <Chip
                  key={opt.key}
                  label={opt.label}
                  onClick={() => setBucket(opt.key)}
                  color={bucket === opt.key ? 'primary' : 'default'}
                  variant={bucket === opt.key ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Stack>
          </>
        );
      })()}

      {/* Search and Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search by SKU or warehouse..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          size="small"
          sx={{ width: 300 }}
        />
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadData}
          variant="outlined"
          size="small"
        >
          Refresh
        </Button>
        <Button
          startIcon={reconciliationRunning ? <CircularProgress size={16} /> : <PlayIcon />}
          onClick={handleRunReconciliation}
          variant="contained"
          color="primary"
          size="small"
          disabled={reconciliationRunning}
        >
          {reconciliationRunning ? 'Running...' : 'Run Reconciliation'}
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          Showing {filteredSkus.length} of {total} unmapped SKUs
        </Typography>
      </Box>

      {/* Unmapped SKUs Table */}
      {filteredSkus.length > 0 ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>Original SKU</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Occurrences</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>First Seen</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSkus.map((sku, idx) => (
                <TableRow key={`${sku.sku}-${sku.warehouse_code}-${idx}`} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {sku.sku}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {sku.original_sku || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={sku.warehouse_code}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={sku.source_type}
                      size="small"
                      color={sku.source_type === 'shipstation' ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="warning.main" fontWeight="medium">
                      {formatNumber(sku.quantity)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatNumber(sku.occurrence_count)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {sku.reason || 'no_batches_found'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {sku.first_seen_at ? new Date(sku.first_seen_at).toLocaleDateString() : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {sku.is_mapped ? (
                      <Chip
                        icon={<CheckIcon />}
                        label={`Mapped to ${sku.mapped_to_sku}`}
                        size="small"
                        color="success"
                      />
                    ) : sku.is_known ? (
                      <Tooltip title="Known product - a shipment went out with no received batch at ship time. Fix the receiving/stock records, not the SKU mapping.">
                        <Chip
                          label="Stock/receiving gap"
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Unknown SKU - map to an existing batch inventory SKU">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LinkIcon />}
                          onClick={() => openMappingDialog(sku)}
                        >
                          Map
                        </Button>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : unmappedSkus.length > 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography color="text.secondary" align="center">
              No SKUs match your search criteria
            </Typography>
          </CardContent>
        </Card>
      ) : null}

      {/* SKU Mapping Dialog */}
      <Dialog
        open={mappingDialogOpen}
        onClose={() => setMappingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon color="primary" />
            Map Shipment SKU to Batch Inventory
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {selectedUnmappedSku && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Unmapped SKU Info */}
              <Alert severity="info">
                <AlertTitle>Shipment SKU: {selectedUnmappedSku.sku}</AlertTitle>
                <Typography variant="body2">
                  This SKU has {formatNumber(selectedUnmappedSku.quantity)} units across {formatNumber(selectedUnmappedSku.occurrence_count)} shipments
                  that could not be deducted from batch inventory.
                </Typography>
                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                  Warehouse: {selectedUnmappedSku.warehouse_code} | Source: {selectedUnmappedSku.source_type}
                </Typography>
                {selectedUnmappedSku.original_sku && selectedUnmappedSku.original_sku !== selectedUnmappedSku.sku && (
                  <Typography variant="caption" component="div">
                    Original SKU: {selectedUnmappedSku.original_sku}
                  </Typography>
                )}
              </Alert>

              {/* Inventory SKU Selection */}
              <Typography variant="subtitle2" gutterBottom>
                Select the batch inventory SKU this shipment SKU should map to:
              </Typography>
              <Autocomplete
                options={inventorySkuOptions}
                getOptionLabel={(option) => `${option.primary_sku} - ${option.product_name || ''}`}
                value={selectedInventorySku}
                onChange={(event, newValue) => setSelectedInventorySku(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Batch Inventory SKU"
                    placeholder="Search batch inventory SKUs..."
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {inventorySkuLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id || option.primary_sku}>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {option.primary_sku}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.product_name}
                      </Typography>
                    </Box>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText={inventorySkuLoading ? "Loading..." : "No matching batch inventory SKUs found"}
                loading={inventorySkuLoading}
                disabled={inventorySkuLoading}
              />

              {selectedInventorySku && (
                <Alert severity="success">
                  <AlertTitle>Mapping Preview</AlertTitle>
                  <Typography variant="body2">
                    <strong>{selectedUnmappedSku.sku}</strong> (shipment SKU) will be linked to <strong>{selectedInventorySku.primary_sku}</strong> (batch inventory SKU)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    After mapping, run reconciliation again to deduct stock from the correct batches.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingDialogOpen(false)} disabled={mappingLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<LinkIcon />}
            onClick={handleSubmitMapping}
            disabled={!selectedInventorySku || mappingLoading}
          >
            {mappingLoading ? <CircularProgress size={20} /> : 'Create Mapping'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ReconciliationUnmappedSkus;
